import {
  Button,
  Divider,
  DropdownButton,
  DropdownButtonContext,
  EmptyState,
  FlexBox,
  Icon,
  IconButton,
  Spinner,
  Text,
  SimplePagination,
  Tooltip,
} from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { HookFormText } from 'lib-shared-hook-form';
import moment from 'moment';
import { useContext, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useUpdateEffect } from 'react-use';

import { aiAgentsApi } from 'apis/aiAgents';
import { usePaginatedEndpoint } from 'utils/apis/pagination/usePaginatedEndpoint';

import { useSidekickChat } from '../../sidekickChat.context';

import styles from './sidekickHistory.module.scss';

const PAGE_SIZE = 8;

type SidekickHistoryPanelForm = {
  searchTerm: string;
};

export const SidekickHistoryPanel = (): JSX.Element => {
  const { chatId, startNewChat, selectChat } = useSidekickChat();
  const { closeDropdown } = useContext(DropdownButtonContext);

  const {
    items: chats,
    total,
    arg,
    asyncStatus,
    changeArg,
    hasNextPage,
    hasPreviousPage,
    nextPage,
    previousPage,
  } = usePaginatedEndpoint({
    endpoint: aiAgentsApi.endpoints.readAiAgentsSidekickConversations,
    initialArg: {
      limit: PAGE_SIZE,
      offset: 0,
    },
    itemsSelector: (response) => response.data.conversations,
    totalSelector: (response) => response.meta.total,
    infinite: false,
  });

  const offset = Number(arg?.offset ?? 0);
  const isLoading =
    asyncStatus === 'pending' || asyncStatus === 'uninitialized';

  const { control, setValue, watch } = useForm<SidekickHistoryPanelForm>({
    defaultValues: { searchTerm: '' },
  });
  const watchSearchTerm = watch('searchTerm');

  const isSearching = useMemo(() => {
    return watchSearchTerm.trim().length > 0;
  }, [watchSearchTerm]);

  const matchCopy = useMemo(() => {
    if (total === 1) return 'match';
    return 'matches';
  }, [total]);

  useUpdateEffect(() => {
    changeArg(
      { searchTerm: watchSearchTerm, offset: 0 },
      { debounceTime: 300 },
    );
  }, [changeArg, watchSearchTerm]);

  const handleClearSearch = () => {
    setValue('searchTerm', '');
  };

  return (
    <FlexBox flexDirection="column" gap="200" className={styles.panel}>
      <Button
        variant="secondary"
        size="large"
        onClick={() => {
          startNewChat();
          closeDropdown();
        }}
      >
        New chat
      </Button>

      <HookFormText
        name="searchTerm"
        control={control}
        componentProps={{
          placeholder: 'Search chats...',
          prefix: {
            type: 'custom',
            value: (
              <FlexBox alignItems="center" padding="0 250">
                <Icon name="search" color="secondary" />
              </FlexBox>
            ),
            noWrap: true,
          },
          suffix: watchSearchTerm
            ? {
                type: 'custom',
                value: (
                  <FlexBox alignItems="center" padding="0 100">
                    <IconButton
                      variant="tertiary"
                      icon="close"
                      onClick={handleClearSearch}
                    />
                  </FlexBox>
                ),
                noWrap: true,
              }
            : undefined,
        }}
        noLabel
      />

      <FlexBox
        justifyContent="space-between"
        alignItems="center"
        padding="0 200"
      >
        <Text.Paragraph as="span" variant="3">
          {isSearching ? 'Search results' : 'Recent chats'}
        </Text.Paragraph>
        <Text.Paragraph as="span" variant="4" subdued>
          {isSearching ? `${total} ${matchCopy}` : 'Latest first'}
        </Text.Paragraph>
      </FlexBox>

      <FlexBox flexDirection="column" gap="0" className={styles.list}>
        {isLoading && (
          <FlexBox justifyContent="center" alignItems="center" flex="1">
            <Spinner />
          </FlexBox>
        )}

        {!isLoading && chats.length === 0 && (
          <FlexBox justifyContent="center" alignItems="center" flex="1">
            <EmptyState
              icon={null}
              title={isSearching ? 'No chats found' : 'No chats yet'}
              description={
                isSearching
                  ? 'Try a different search.'
                  : 'Ask Mim something to start one.'
              }
            />
          </FlexBox>
        )}

        {!isLoading &&
          chats.map((chat) => {
            const isCurrent = chat.id === chatId;

            return (
              <FlexBox
                key={chat.id}
                as="button"
                gap="200"
                alignItems="center"
                className={classnames(styles.row, {
                  [styles.rowCurrent]: isCurrent,
                })}
                onClick={() => {
                  selectChat(chat.id);
                  closeDropdown();
                }}
              >
                <Icon name="chat" size={16} className={styles.rowIcon} />
                <FlexBox flexDirection="column" className={styles.rowText}>
                  <Text.Paragraph
                    as="span"
                    variant="3"
                    className={styles.rowTitle}
                  >
                    {chat.title ?? 'Untitled chat'}
                  </Text.Paragraph>
                  <Text.Paragraph as="span" variant="4" subdued>
                    {moment(chat.updatedAt).fromNow()}
                    {isCurrent ? ' · Current' : ''}
                  </Text.Paragraph>
                </FlexBox>
              </FlexBox>
            );
          })}
      </FlexBox>

      <Divider />

      <FlexBox
        justifyContent="space-between"
        alignItems="center"
        padding="0 200"
      >
        <Text.Paragraph as="span" variant="4" subdued>
          {!total
            ? '0 of 0'
            : `${offset + 1}–${offset + chats.length} of ${total}`}
        </Text.Paragraph>
        <SimplePagination
          previousButtonText="Previous"
          nextButtonText="Next"
          previousButtonDisabled={!hasPreviousPage}
          nextButtonDisabled={!hasNextPage}
          previousButtonClick={previousPage}
          nextButtonClick={nextPage}
        />
      </FlexBox>
    </FlexBox>
  );
};

export const SidekickHistory = (): JSX.Element => (
  <Tooltip content="Chat history">
    <DropdownButton
      icon="history"
      noArrow
      fixed
      destroyOnClose
      buttonProps={{ variant: 'secondary' }}
      // The floating panel drags by its header, which this dropdown sits inside.
      onPointerDown={(event) => event.stopPropagation()}
      dropdown={<SidekickHistoryPanel />}
    />
  </Tooltip>
);
